export type ProjectGroup =
  | "Configurators & Systems"
  | "Dashboards"
  | "AI & Chatbots"
  | "Web Platforms";

export type ProjectImage = { src: string; alt: string };

export type Project = {
  id: string;
  slug: string; // URL of the case-study page: /projects/<slug>
  group: ProjectGroup;
  category?: string;
  title: string;
  description: string;
  image: string; // cover image, used on cards
  imageAlt?: string;
  gallery?: (string | ProjectImage)[]; // optional extra screenshots (any number); objects let you set alt text
  tags?: string[];
  technologies?: string[];
  demoUrl?: string;
  client?: string;
  // Optional SEO / case-study content for the project page
  seoTitle?: string;
  seoDescription?: string;
  overview?: string[];
  features?: string[];
};

export const projectGroups: ProjectGroup[] = [
  "Configurators & Systems",
  "Dashboards",
  "AI & Chatbots",
  "Web Platforms",
];

// Cover first, then any extra gallery images — always with alt text
export function projectImages(p: Pick<Project, "title" | "image" | "imageAlt" | "gallery">): ProjectImage[] {
  const { name, kind } = splitTitle(p.title);
  const cover = { src: p.image, alt: p.imageAlt ?? `${name}${kind ? ` — ${kind}` : ""}, built by Subhan Nadeem` };
  const extra = (p.gallery ?? []).map((g, i) =>
    typeof g === "string" ? { src: g, alt: `${name} screenshot ${i + 2}, built by Subhan Nadeem` } : g
  );
  return [cover, ...extra];
}

// Technologies + tags merged, without case-only duplicates ("Node Js" / "Node JS")
export function projectStack(p: Pick<Project, "technologies" | "tags">) {
  const seen = new Set<string>();
  return [...(p.technologies ?? []), ...(p.tags ?? [])].filter((t) => {
    const key = t.toLowerCase().replace(/[\s.]/g, "");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Shown as the large card at the top of /projects
export const featuredProjectId = "Ralux Solar";

// "OptionsSight - Options Flow Dashboard" -> { name: "OptionsSight", kind: "Options Flow Dashboard" }
export function splitTitle(title: string) {
  const [name, ...rest] = title.split(" - ");
  return { name, kind: rest.join(" - ") };
}

export const projects: Project[] = [
  {
    id: "Ralux Solar",
    slug: "ralux-solar-configurator",
    group: "Configurators & Systems",
    category: "Solar · Configurator",
    title: "Ralux Solar Configurator - Fullstack Application",
    description:
      "Developed a comprehensive solar mounting system configurator with admin panel for Ralux Solar, designed to analyze location-based data and generate optimized solar mounting solutions. The platform intelligently suggests suitable mounting systems based on customer area, environmental conditions, and regional standards, ensuring structural reliability and cost efficiency. Engineered an advanced calculation engine in Node.js to handle diverse country-specific structural load formulas across 20+ mounting systems and their variants. Implemented interactive map drawing tools allowing users to select their installation area and place solar panels directly on the map. Automatically generated 2D mounting system layouts based on user inputs and panel configurations. Automated Bill of Materials (BOM) generation based on the configured design, providing cost and material breakdowns instantly.",
    image: "/projects/ralux-solar-app-bom.png",
    imageAlt: "Ralux Solar configurator: bill of materials step with project summary, built by Subhan Nadeem",
    gallery: [
      {
        src: "/projects/ralux-solar-layout-bom.png",
        alt: "Solar mounting layout sheet with rail cutting combinations and bill of materials, generated by the Ralux Solar configurator",
      },
      {
        src: "/projects/ralux-solar-structure-views.png",
        alt: "Side, top and isometric views of a solar racking structure generated by the Ralux Solar configurator",
      },
    ],
    client: "Ralux Solar Racking Systems",
    seoTitle: "Ralux Solar Racking Configurator — Case Study",
    seoDescription:
      "How Subhan Nadeem built the Ralux Solar racking configurator: map-based panel layout, structural load engine for 20+ systems, 2D layouts, BOM and PDF reports.",
    overview: [
      "Ralux Solar Racking Systems wanted installers to configure a solar mounting system online and get an engineered layout, bill of materials and cost breakdown instantly. I designed and built the full-stack application: the customer-facing configurator, the structural calculation engine and the admin panel.",
      "A project moves through five steps: Project Setup, System Configuration, Project Design, Installation Drawing and Reports & Downloads. The configurator suggests suitable mounting systems from the customer's location, environmental conditions and regional standards, then generates everything needed to quote and install.",
    ],
    features: [
      "Map drawing tools to select the installation area and place solar panels directly on the map",
      "Node.js calculation engine for country-specific structural load formulas (such as ASCE 7) across 20+ mounting systems and their variants",
      "Mounting-system suggestions based on location, environmental conditions and regional standards",
      "Automatic 2D mounting layouts with side, top and isometric views",
      "Bill of materials with part codes, quantities and weights, plus rail-cutting combinations",
      "Downloadable PDF reports: layout, bill of materials and engineering report",
      "Multilingual output in English, Spanish and Italian",
      "Admin panel to manage mounting systems, variants and project data",
    ],
    tags: ["Next.js","supabase", "Node JS", "Tailwind CSS","ShadCN", "JavaScript"],
    demoUrl: "https://ralux-solar.vercel.app/",
    technologies: ["Node Js", "Next.js", "supabase", "Mantine UI", "JavaScript", "CSS"],
  },
  {
    id: "NatPower",
    slug: "natpower-energy",
    group: "Web Platforms",
    category: "Energy · Web platform",
    title: "NatPower Energy - Web Application",
    description:
      "I successfully developed a fully animated web application for NatPower, a leading UK-based energy company. My primary focus was on creating an engaging and visually appealing user experience. The project included multiple dynamic pages, requiring advanced data handling to display real-time information effectively. The application featured four parks, each containing six pages, all dynamically managed to ensure seamless content updates. This project enhanced my expertise in front-end design, animations, and handling complex dynamic data structures.",
    image: "/projects/natpower.png",
    tags: ["Next.js", "Tailwind CSS","Shadcn UI", "MapBox", "JavaScript"],
    demoUrl: "https://www.energyvestors.com/",
    technologies: ["Next.js", "Shadcn UI", "Tailwind CSS","MapBox", "JavaScript"],
  },
  {
    id: "OptionsSight",
    slug: "optionssight-trading-dashboard",
    group: "Dashboards",
    category: "Fintech · Dashboard",
    title: "OptionsSight - Options Flow Trading Dashboard & Design System",
    description:
      "OptionsSight is a modern options-flow trading dashboard that helps traders see where market activity is concentrated through live-style analytics, heatmaps, and social sentiment insights. I built the frontend in Next.js 16 and Tailwind CSS v4 as a pixel-accurate implementation of the Figma design, which also serves as the design-system source of truth for the production dashboard. Key modules include the Heat Seeker strike/expiry heatmap, Surge premium-flow chart, Dealer Map (GEX) positioning chart, Volume Shift call/put spike panel, Top Movers, High Vol Cheaplies & Leaps tables, Live Flow and Flow History tables, and a Social Sentiments section with Trending, Sentiment, Themes (word cloud), and Buzz News views. I also built a customizable drag-and-drop widget dashboard, a complete auth and multi-step onboarding flow, settings and billing pages, and a token-based design system with dark/light themes, reusable master components, a live component showcase, and fully responsive layouts across five breakpoints from mobile to 1920px.",
    image: "/projects/options-sight.png",
    gallery: [
      {
        src: "/projects/options-sight-dashboard.png",
        alt: "OptionsSight live flow dashboard with surge chart, live order flow table and market sentiment gauge",
      },
    ],
    demoUrl: "https://www.optionssight.com/",
    tags: ["Next.js", "React", "TypeScript", "Tailwind CSS", "shadcn/ui", "Lightweight Charts"],
    technologies: ["Next.js", "TypeScript", "Tailwind CSS", "shadcn/ui", "Recharts", "React Grid Layout"],
  },
  {
  id: "Flowtopia",
  slug: "flowtopia-options-analytics",
    group: "Dashboards",
    category: "Fintech · AI",
  title: "Flowtopia - Options Flow Analytics & Trader Intelligence Platform",
  description:
    "Purpose: Flowtopia is built to help traders analyze options flow, monitor market sentiment, and learn data-driven trading strategies through one interactive platform. It is an ongoing web application focused on delivering a modern trading dashboard experience with features such as ticker insights, training content, notifications, and AI-assisted tools. In this project, I have been contributing to the frontend development and API integrations, with a strong focus on the ticker dashboard, notifications feature, training module, and AI bot feature. My work involves building responsive and user-friendly interfaces, integrating APIs and live data flows, and improving the overall usability of the platform as it continues to evolve.",
  image: "/projects/flowtopia.png",
  tags: ["Next.js", "React", "Mantine UI", "Redux Toolkit", "OpenAI API", "Highcharts"],
  demoUrl: "https://www.flowtopia.co/",
  technologies: ["Next.js", "Stripe", "Aws", "Redux Toolkit", "OpenAI", "Highcharts"],
},
  {
    id: "AlphaPadel",
    slug: "alpha-padel-tournament-management",
    group: "Configurators & Systems",
    category: "Management system",
    title: "Alpha Padel Tournaments - Fullstack Web Application",
    description:
      "Developed a dynamic web application for tennis match management, featuring user authentication, tournament creation, player tracking, and ground booking. Includes an intuitive admin dashboard for efficient data management and analytics, ensuring secure and scalable functionality.",
    image: "/projects/alpha-padel.png",
    tags: ["Next.js","supabase", "Node JS", "Tailwind CSS","Mantine UI", "JavaScript"],
    demoUrl: "https://www.alphapadel.app/",
    technologies: ["Node Js", "Next.js", "supabase", "Mantine UI", "JavaScript", "CSS"],
  },
  {
    id: "ChatAI",
    slug: "chatai-custom-chatbot-saas",
    group: "AI & Chatbots",
    category: "AI · SaaS",
    title: "ChatAI - Create Custom Chatbot SaaS Platform",
    description:
      "A modern SaaS solution developed with Next.js, Supabase, TypeScript, LangChain, and OpenAI API, empowering users to create fully personalized AI chatbots. Users can design chatbots by customizing names, avatar, chat starters, personas, and instructions during a seamless onboarding process. The platform allows content uploads via PDF, enabling chatbots to deliver responses based on user-provided data. With built-in authentication (signup/login), Stripe payment integration, and a user-friendly dashboard for managing chatbot settings, users can easily fine-tune their chatbots. Each chatbot comes with a unique URL for effortless integration into any website.",
    image: "/projects/chatbot-thumbnail.png",
    tags: ["Node Js", "Next.js", "supabase", "ShadCN UI", "Open AI", "Tailwind CSS"],
    demoUrl: "https://ai-bot-v2.vercel.app/",
    technologies: ["Node Js", "Next.js", "supabase", "ShadCN UI", "Open AI", "Tailwind CSS"],
  },
  {
    id: "podcastAI",
    slug: "pod-transcripts-ai-chatbot",
    group: "AI & Chatbots",
    category: "AI · Chatbot",
    title: "Pod Transcripts AI Chatbot Application",
    description:
      "Podcast Transcript AI Chatbot using Vercel AI SDK, Langchain & Supabase. I built a smart AI chatbot for a podcast website featuring popular episodes with detailed transcripts. The chatbot uses Vercel AI SDK, Langchain, and Supabase vector database for efficient and accurate responses. Key Features: Natural Language Querying: Users can ask questions in natural language, and the chatbot responds using data stored from podcast transcript PDFs. Contextual Answers: Uses Langchain and embeddings to understand and respond with accurate information sourced from stored vectors in Supabase. Related Podcast Recommendations: In addition to answering questions, the chatbot suggests relevant podcast episodes with clickable links that navigate directly to the episode’s transcript page. This solution is ideal for content-rich platforms like podcasts, video courses, or blogs that want to provide an interactive experience through AI-driven search and discovery.",
    image: "/projects/pod-ai.png",
    tags: ["Vercel SDK AI", "Langchain", "Next.js", "supabase"],
    demoUrl: "https://www.thepodtranscripts.com/",
    technologies: ["Vercel SDK AI", "Langchain", "Next.js", "supabase"],
  },
  {
    id: "thr",
    slug: "the-hockey-review",
    group: "Web Platforms",
    category: "SaaS · Platform",
    title: "The Hockey Review - Fullstack Web Application",
    description:
      "JHR aims to be the quintessential online destination for hockey-related data and community insights. The platform empowers users to access detailed information about hockey leagues and teams, submit and view ratings and reviews, and engage with educational content. JHR supports distinct user roles, accommodating both individual enthusiasts and team representatives. Users can create profiles, interact with content, and participate in community discussions, while team owners have enhanced capabilities to manage their team's presence and connect with their audience. Premium subscription options provide additional features and benefits. JHR is designed to be adaptive, user-friendly, and secure, ensuring a seamless and valuable experience for all stakeholders.",
    image: "/projects/thr.png",
    tags: ["Next.js", "supabase", "stripe", "tailwind css", "node js"],
    demoUrl: "https://www.thehockeyreview.com/",
    technologies: ["Next.js", "supabase", "stripe", "tailwind css", "node js"],
  },
  {
    id: "sora",
    slug: "sora-ai-video-platform",
    group: "AI & Chatbots",
    category: "AI · Frontend",
    title: "Sora Ai - Frontend Development",
    description:
      "The platform is a user-centric frontend application for AI video generation, enabling users to create accounts, top up balances, and generate videos using reference images or videos. It features a responsive, mobile-friendly design with dark and light modes. Users can manage their generated videos, view invoices, and monitor costs efficiently. The dashboard provides streamlined navigation with a sidebar, balance overview, and settings. Top-up and video generation flows include clear cost breakdowns and step-wise guidance, ensuring a seamless, secure, and intuitive experience for all users.",
    image: "/projects/sora.png",
    tags: ["Next.js", "Tailwind CSS","shadcn", "JavaScript"],
    demoUrl: "https://sora2.nl/",
    technologies: ["JavaScript", "Next.js", "Shadcn UI", "Tailwind CSS"],
  },
  {
    id: "mocha-coin",
    slug: "mocha-coin",
    group: "Web Platforms",
    category: "Web3 · Frontend",
    title: "Mocha Coin - Frontend Development",
    description:
      "Developed a fully mobile-responsive crypto coin website with an animated landing page using Next.js and shadcn. Integrated wallet connection features for Phantom, Trust, and Solflare, enabling smooth user interactions. The project also included seamless backend integration for a secure and efficient experience.",
    image: "/projects/mocha.png",
    tags: ["Next.js", "Tailwind CSS", "JavaScript"],
    demoUrl: "https://solana-crypto-web.vercel.app/",
    technologies: ["JavaScript", "Next.js", "Mantine UI", "CSS"],
  },
  {
    id: "GemHunters",
    slug: "gemhunters-affiliate-program",
    group: "Web Platforms",
    category: "Frontend",
    title: "GemHunters - Affiliate Program Frontend",
    description:
      "In this project, I developed the frontend for the Affiliate Program feature of the GemHunters website, focusing on creating a responsive and visually appealing interface for user interaction",
    image: "/projects/gem.png",
    tags: ["JavaScript", "Next.js", "Mantine UI", "Tailwind CSS"],
    technologies: ["JavaScript", "Next.js", "Mantine UI", "Tailwind CSS"],
    demoUrl: "https://gemhunters.co/",
  },
];
